import { useEffect, useState, useMemo } from "react";
import { BRAND } from "@core/brand/brand-config";
import { useHistory, withRouter, useLocation } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import { globalCountryDetailsDictionary } from "../../core/global/global-country-details";
import "./sidebar.scss";
import { CLOUDINARY_INTEL_BASE_URL, dashboardTabsEnum, getActiveNavItem, SHIPMENTS_NAV_ID, tabsEnum, TabsResourceCenter } from "../../utils/enum";
import { useDispatch, useSelector } from "react-redux";
import { store } from "@redux/store";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { getUserActivePlan } from "@helpers/profileHelper";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { getSidebarCountData } from "@redux/Sidebar/sidebarCount.action";
import { ChevronDown } from "react-feather";
import LocalStorageService from "@core/services/local-storage.service";
import { LOB_ENUM } from "@routes/intel/ProductOnboarding/utils";

const Sidebar = (props) => {
  const history = useHistory();
  const [openDropdowns, setOpenDropdowns] = useState(new Set());
  const [getActivePlan, setGetActivePlan] = useState(false);
  const { userData, creditDetails } = useSelector((state) => state.userDetails);
  const countDetails = useSelector((state) => state.sidebarCount.countDetails);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const GLOBALPLAN = creditDetails?.data?.find((data) => data?.is_active && data?.user_active_plan?.toLowerCase()?.includes("global"))?.navigate === "search";
  const STARTERPLAN = creditDetails?.data?.find((data) => data?.is_active && data?.user_active_plan?.toLowerCase()?.includes("starter"))?.navigate === "search";
  const url = history.location.pathname + history.location.search;
  const isResourceCenter = url.includes("help-center");
  const [activeResourceTab, setActiveResourceTab] = useState(TabsResourceCenter[0].enum);
  const { search } = useLocation();
  const target = new URLSearchParams(search).get("target");
  const {
    userDetails: {
      creditDetails: { planAccess },
    },
  } = store.getState() || {};

  const isPortProfile = planAccess?.is_port_profile ?? false;
  const isTradeLaneProfile = planAccess?.is_tradelane_profile ?? false;

  useEffect(() => {
    const allParentNames = tabsEnum.map((parent) => parent.name);
    const nestedGroupKeys = tabsEnum.flatMap((parent) => (parent.subItem || []).filter((child) => child?.subItem?.length > 0).map((child) => `${parent.name}:${child.name}`));
    setOpenDropdowns(new Set([...allParentNames, ...nestedGroupKeys]));
  }, [tabsEnum]);

  const SIDEBAR_STATE_KEY = "sidebarState";

  const getSidebarStateKey = (sessionId) => {
    return `${SIDEBAR_STATE_KEY}_${sessionId}`;
  };

  const getStoredSidebarState = (sessionId) => {
    const key = getSidebarStateKey(sessionId);
    const storedState = localStorage.getItem(key);
    return storedState ? JSON.parse(storedState) : null;
  };

  const storeSidebarState = (sessionId, isManuallyClosed) => {
    const key = getSidebarStateKey(sessionId);
    localStorage.setItem(key, JSON.stringify({ isManuallyClosed }));
  };

  useEffect(() => {
    if (props.type === undefined && !url.includes("product-trends")) {
      const sessionId = LocalStorageService.getSessionID();
      const storedState = getStoredSidebarState(sessionId);
      if (storedState?.isManuallyClosed) {
        props.setSideBarOpen(false);
        return;
      }

      const isFiltersApplied = props.checkforFilters(props.appliedFilter);
      const shouldClose = (props.isSearchButtonCalled && props.searchedItem) || isFiltersApplied || props.isDateChanged;
      props.setSideBarOpen(!shouldClose);
    }
  }, [props.isSearchButtonCalled, props.appliedFilter, props.searchedItem, props.isDateChanged]);

  const handleSidebarToggle = (newState) => {
    const sessionId = LocalStorageService.getSessionID();

    if (!newState) {
      storeSidebarState(sessionId, true);
    } else {
      storeSidebarState(sessionId, false);
    }
    props.setSideBarOpen(newState);
  };

  const toggleDropdown = (parentName) => {
    setOpenDropdowns((prevOpen) => {
      const newOpen = new Set(prevOpen);
      if (newOpen.has(parentName)) {
        newOpen.delete(parentName);
      } else {
        newOpen.add(parentName);
      }
      return newOpen;
    });
  };
  useEffect(() => {
    if (creditDetails?.header?.length) {
      setGetActivePlan(true);
    }
  }, [creditDetails?.header]);

  useEffect(() => {
    if (Object.keys(countDetails).length === 0) {
      dispatch(getSidebarCountData());
    }
  }, []);

  useEffect(() => {
    setActiveResourceTab(target || TabsResourceCenter[0].enum);
  }, [target]);

  const enums = dashboardTabsEnum.map((tab) => ({ ...tab, visibleCondition: !(tab.hideOnGlobalPlan && GLOBALPLAN) }));

  const activeItemId = getActiveNavItem(pathname)?.id ?? (props.type ? null : SHIPMENTS_NAV_ID);

  const buildQueryWithParams = (link, country, dataSource) => {
    let query = `${link}`;
    if (country || dataSource) {
      query += country && String(country).includes("country=") ? `?${country}&type=${dataSource}` : `?country=${country}&type=${dataSource}`;
    }
    return query;
  };

  // Data source entry for ?country&type, or undefined when either is missing or doesn't match a known data source (e.g. country=null)
  const getDataSourceFromUrl = (searchString) => {
    const params = new URLSearchParams(searchString);
    const country = params.get("country")?.toLowerCase();
    const dataSource = params.get("type")?.toLowerCase();
    if (!country || !dataSource) {
      return undefined;
    }
    return Object.entries(globalCountryDetailsDictionary).find(([key, details]) => {
      const isCountryMatch = [key.split("_")[0], details?.country_small, details?.country_caps, details?.countryShortHand, details?.countryCodeFlag].some((value) => value?.toLowerCase() === country) || details?.countryUrlDashboard?.toLowerCase() === `country=${country}`;
      const isTypeMatch = details?.countryType?.toLowerCase() === dataSource || details?.manyCountryType?.toLowerCase() === dataSource;
      return isCountryMatch && isTypeMatch;
    })?.[1];
  };

  const hermesRedirectionUrl = useMemo(() => {
    const urlDataSource = getDataSourceFromUrl(search);
    let country = urlDataSource?.countryUrlDashboard;
    let dataSource = urlDataSource?.countryType;

    if (!country || !dataSource) {
      const normalizedCurrentPath = pathname.replace(/^\/+|\/+$/g, "").toLowerCase();
      for (let property in globalCountryDetailsDictionary) {
        const normalizedCountryPath = (globalCountryDetailsDictionary[property].countryPathName || "").replace(/^\/+|\/+$/g, "").toLowerCase();
        if (normalizedCountryPath && (normalizedCurrentPath === normalizedCountryPath || normalizedCurrentPath.startsWith(`${normalizedCountryPath}/`))) {
          country = globalCountryDetailsDictionary[property].countryUrlDashboard;
          dataSource = globalCountryDetailsDictionary[property].countryType;
          break;
        }
      }
    }

    if (!country || !dataSource) {
      return "/hermes-chat";
    }
    return buildQueryWithParams("/hermes-chat", country, dataSource);
  }, [pathname, search]);

  // Shipments search page for the current ?country&type; falls back to the first subscribed data source when the params are missing or invalid (e.g. country=null),
  // and with no subscribed data source to the brand's all-data search: all-country shipments on Intel, global search on Source Atlas
  const getShipmentsLink = () => {
    const countryDetails = Object.values(globalCountryDetailsDictionary);
    const matchedFromUrl = getDataSourceFromUrl(history.location.search);
    const firstSubscribed = countryDetails.find((details) => {
      const productId = details?.product_id?.toLowerCase();
      return productId && creditDetails?.header?.some((header) => header?.is_subscribed && header?.product_id?.toLowerCase() === productId);
    });
    const target = matchedFromUrl || firstSubscribed;
    if (target) {
      return `/${target.countryPathName}`;
    }
    return BRAND.key === "source_atlas" ? "/global-search" : `/${globalCountryDetailsDictionary.multi_country.countryPathName}`;
  };

  const changeTab = (value) => {
    let country = "";
    let dataSource = "";
    let link = "";
    let query = "";
    const normalizedCurrentPath = history.location.pathname.replace(/^\/+|\/+$/g, "").toLowerCase();
    if (value.subItem && value.subItem.length > 0) {
      return;
    }

    const targetPath = (value.link || "").replace(/^\/+|\/+$/g, "").toLowerCase();
    const isOnSubRouteOfTarget = !!targetPath && normalizedCurrentPath !== targetPath && normalizedCurrentPath.startsWith(`${targetPath}/`);

    if (activeItemId !== value.id || isOnSubRouteOfTarget) {
      for (let property in globalCountryDetailsDictionary) {
        let item = property;
        const normalizedCountryPath = (globalCountryDetailsDictionary[item].countryPathName || "").replace(/^\/+|\/+$/g, "").toLowerCase();
        const isMatchingCountryPath = normalizedCurrentPath === normalizedCountryPath || normalizedCurrentPath.startsWith(`${normalizedCountryPath}/`);
        if (isMatchingCountryPath && value.id !== SHIPMENTS_NAV_ID) {
          country = globalCountryDetailsDictionary[item].countryUrlDashboard;
          dataSource = globalCountryDetailsDictionary[item].countryType;
          link = value.link;
          break;
        }
      }
      if (value.pendo_event && value.pendo_event !== "") {
        trademoTrackingEvents(value.pendo_event, generateTrackingObject("intel-dashboard", pathname, getUserActivePlan()));
      }

      if (value.id === SHIPMENTS_NAV_ID) {
        return (window.location.href = getShipmentsLink());
      }

      if (!country && !dataSource && value.id !== SHIPMENTS_NAV_ID) {
        const params = new URLSearchParams(history.location.search);
        const urlDataSource = getDataSourceFromUrl(history.location.search);
        link = value.link;
        if (urlDataSource) {
          country = urlDataSource.countryUrlDashboard;
          dataSource = urlDataSource.countryType;
        } else if (!params.has("country") && !params.has("type")) {
          link = value.link + history.location.search;
        }
      }
      query = buildQueryWithParams(link, country, dataSource);
      history.push(query);
    }
  };

  const changeTabResCenter = (tab) => {
    setActiveResourceTab(tab.enum);
    history.push(tab.url);
  };

  return (
    <div>
      <div className="layout-container d-flex">
        {!window.location.href.includes("/free-trial") ? (
          <div className={`side-bar ${isResourceCenter ? "" : "tw-bg-gray-100"} tw-flex tw-flex-col  ${props.sideBarOpen ? "left_open" : "left_close"}`}>
            <a className={`tw-shadow-header tw-h-12 tw-flex tw-items-center tw-gap-2 tw-p-3 ${props.sideBarOpen ? "tw-justify-start" : "tw-justify-center"}`} href={hermesRedirectionUrl} onClick={() => trademoTrackingEvents(trackingEventDictionary.hermesChatSidebarClick, generateTrackingObject("intel-dashboard", pathname, getUserActivePlan()))}>
              <div>
                <img src={BRAND.logo} alt="dashboard logo" />
              </div>
              {props.sideBarOpen && <div className="tw-text-base tw-font-semibold tw-text-gray-900">{BRAND.name}</div>}
            </a>
            <div className="tw-flex tw-flex-col tw-justify-between tw-p-4 tw-pl-0 tw-pr-[4px] tw-h-[95vh] tw-overflow-auto">
              {isResourceCenter ? (
                <div className="tw-flex tw-flex-col tw-gap-1 tw-rounded-lg tw-px-1 tw-py-1.5 tw-cursor-pointer">
                  {TabsResourceCenter.map((tab) =>
                    tab.isVisible ? (
                      <div key={tab} onClick={() => changeTabResCenter(tab)} className={`${activeResourceTab === tab.enum ? "tw-bg-gray-200" : ""} tw-gap-1 tw-py-1.5 tw-px-2 tw-flex tw-items-center tw-border-none tw-rounded-lg tw-text-sm tw-font-medium`}>
                        <span className="material-symbols-outlined mr-1">{tab.icon}</span>
                        <span>{tab.name}</span>
                      </div>
                    ) : (
                      <></>
                    )
                  )}
                </div>
              ) : (
                <div className="tw-h-full tw-flex tw-flex-col tw-justify-between">
                  <div className={`side-bar-bottom ${props.sideBarOpen ? "" : "tw-flex tw-flex-col tw-items-center"}`}>
                    <span onClick={() => handleSidebarToggle(!props.sideBarOpen)} className="m-1 tw-cursor-pointer">
                      {props?.sideBarOpen ? (
                        <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
                          <path d="M629.92-340.54v-278.92L478.08-480l151.84 139.46ZM223.31-164q-24.56 0-41.94-17.37Q164-198.75 164-223.31v-513.38q0-24.56 17.37-41.94Q198.75-796 223.31-796h513.38q24.56 0 41.94 17.37Q796-761.25 796-736.69v513.38q0 24.56-17.37 41.94Q761.25-164 736.69-164H223.31ZM336-216v-528H228.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v503.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85H336Zm52 0h343.69q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-503.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H388v528Zm-52 0H216h120Z" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
                          <path d="M502.08-619.46v278.92L653.92-480 502.08-619.46ZM223.31-164q-24.56 0-41.94-17.37Q164-198.75 164-223.31v-513.38q0-24.56 17.37-41.94Q198.75-796 223.31-796h513.38q24.56 0 41.94 17.37Q796-761.25 796-736.69v513.38q0 24.56-17.37 41.94Q761.25-164 736.69-164H223.31ZM336-216v-528H228.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v503.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85H336Zm52 0h343.69q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-503.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H388v528Zm-52 0H216h120Z" />
                        </svg>
                      )}
                    </span>
                    {getActivePlan && (
                      <div className={`${props.sideBarOpen ? "sidebar_wrapper" : "sidebar_wrapper_close"} tw-transition-all`}>
                        {tabsEnum.map((parent) => {
                          const isGroup = parent?.subItem?.length > 0;
                          return (
                            <div key={parent.name}>
                              {!isGroup ? (
                                <div className={`list_container my-1 ${activeItemId === parent.id ? "active-tab" : ""}`}>
                                  <div id={parent?.id} onClick={() => changeTab(parent)} className={`products-list ${!props.sideBarOpen ? "justify-content-center" : ""}`} data-tip={parent.name} data-for={`${parent.name}dash`}>
                                    {parent.icon}
                                    {!props.sideBarOpen ? (
                                      <ReactTooltip className="trademo-tooltip" textColor="#FFFFFF" place="bottom" id={`${parent.name}dash`} />
                                    ) : (
                                      <>
                                        <p>{parent.name}</p>
                                        {parent.beta_icon && <img className="ml-1" src={`${CLOUDINARY_INTEL_BASE_URL}/v1768541879/intel/misc/BETA_ICON_hqpp9y.svg`} />}
                                      </>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className={`accordion-item ${!props.sideBarOpen ? "d-none" : ""}`}>
                                    <div onClick={() => toggleDropdown(parent.name)} className="product-category" data-tip={parent.name} data-for={`${parent.name}dash`}>
                                      {props.sideBarOpen && (
                                        <span className={`accordion-arrow ${openDropdowns.has(parent.name) ? "open" : ""}`}>
                                          <ChevronDown size={16} />
                                        </span>
                                      )}
                                      {!props.sideBarOpen ? (
                                        <ReactTooltip className="trademo-tooltip" textColor="#FFFFFF" place="bottom" id={`${parent.name}dash`} />
                                      ) : (
                                        <>
                                          <p>{parent?.name ?? ""}</p>
                                          {parent.beta_icon && <img className="ml-1" src={`${CLOUDINARY_INTEL_BASE_URL}/v1768541879/intel/misc/BETA_ICON_hqpp9y.svg`} />}
                                        </>
                                      )}
                                      {!props.sideBarOpen && parent.icon}
                                    </div>
                                  </div>
                                  {(openDropdowns.has(parent.name) || !props.sideBarOpen) && (
                                    <div className={`accordion-content open ${props.sideBarOpen ? "with-border" : ""}`}>
                                      {parent.subItem.map((child) => {
                                        if ((child.id === "hs_classifier" || child.id === "compare_tariff") && GLOBALPLAN && BRAND.key !== "source_atlas") return;
                                        if ((child.id === "company_search" || child.id === "product_search") && STARTERPLAN) return;
                                        if (child.id === "intel_hub" && ![LOB_ENUM.BUSINESS_DEVELOPMENT, LOB_ENUM.EXPORT, LOB_ENUM.IMPORT, LOB_ENUM.EXPORT_AND_IMPORT].includes(userData?.line_of_business)) return null;
                                        const isChildGroup = child?.subItem?.length > 0;
                                        const childGroupKey = `${parent.name}:${child.name}`;
                                        if (isChildGroup) {
                                          return (
                                            <div key={child.name}>
                                              <div className={`list_container d-flex`}>
                                                <div id={child?.id} onClick={() => props.sideBarOpen && toggleDropdown(childGroupKey)} className={`products-list`} data-tip={child.name} data-for={`${child.name}dash`}>
                                                  {child.icon}
                                                  {!props.sideBarOpen ? (
                                                    <ReactTooltip className="trademo-tooltip" textColor="#FFFFFF" place="bottom" id={`${child.name}dash`} />
                                                  ) : (
                                                    <>
                                                      <p>{child.name}</p>
                                                      <span className={`accordion-arrow ${openDropdowns.has(childGroupKey) ? "open" : ""}`}>
                                                        <ChevronDown size={16} />
                                                      </span>
                                                    </>
                                                  )}
                                                </div>
                                              </div>
                                              {(openDropdowns.has(childGroupKey) || !props.sideBarOpen) && (
                                                <div className={`accordion-content open ${props.sideBarOpen ? "with-border" : ""}`}>
                                                  {child.subItem.map((grandchild) => (
                                                    <div key={grandchild.name} className={`list_container my-1 ${activeItemId === grandchild.id ? "active-tab" : ""} ${(!isPortProfile && grandchild.name === "Ports") || (!isTradeLaneProfile && grandchild.name === "Trade Lanes") ? "d-none" : "d-flex"}`}>
                                                      <div id={grandchild?.id} onClick={() => changeTab(grandchild)} className={`products-list`} data-tip={`Bookmarked ${grandchild.name}`} data-for={`${grandchild.name}dash`}>
                                                        {grandchild.icon}
                                                        {!props.sideBarOpen ? <ReactTooltip className="trademo-tooltip" textColor="#FFFFFF" place="bottom" id={`${grandchild.name}dash`} /> : <p>{grandchild.name}</p>}
                                                      </div>
                                                      {props.sideBarOpen && grandchild.count && countDetails?.[grandchild.count_key] !== 0 && (
                                                        <div>
                                                          <span className="dashboard_count d-flex justify-content-center">{countDetails?.[grandchild.count_key]}</span>
                                                        </div>
                                                      )}
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        }
                                        return (
                                          <div key={child.name} className={`list_container my-1 ${activeItemId === child.id ? "active-tab" : ""} ${!isPortProfile && child.name === "Ports" ? "d-none" : "d-flex"}`}>
                                            <div id={child?.id} onClick={() => changeTab(child)} className={`products-list`} data-tip={parent.name === "Bookmarks" ? `Bookmarked ${child.name}` : child.name} data-for={`${child.id}dash`}>
                                              {child.icon}
                                              {!props.sideBarOpen ? (
                                                <ReactTooltip className="trademo-tooltip" textColor="#FFFFFF" place="bottom" id={`${child.id}dash`} />
                                              ) : (
                                                <>
                                                  <p>{child.name}</p>
                                                  {child.new_icon && <img className="ml-1" src="https://res.cloudinary.com/drzta9shk/image/upload/v1671105392/intel/icons/icon-new-badge_gdiz3b.svg" />}
                                                  {child.beta_icon && <img className="ml-1" src="https://res.cloudinary.com/drzta9shk/image/upload/v1768541879/intel/misc/BETA_ICON_hqpp9y.svg" />}
                                                </>
                                              )}
                                            </div>
                                            {props.sideBarOpen && child.count && countDetails?.[child.count_key] !== 0 && (
                                              <div>
                                                <span className="dashboard_count d-flex justify-content-center">{countDetails?.[child.count_key]}</span>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div>
                    {enums.map((tab) => {
                      return (
                        tab.visibleCondition && (
                          <div key={tab.name} className={`list_container my-1 ${activeItemId === tab.id ? "active-tab" : ""}`}>
                            <div className={`products-list ${!props.sideBarOpen ? "justify-content-center" : ""}`} onClick={() => changeTab({ id: tab.id, name: tab.name, link: tab.link, pendo_event: tab.pendoEvent })} data-tip={tab.name} data-for={tab.dataFor}>
                              <span className="material-symbols-outlined">{tab.iconEnum}</span>
                              <span
                                className={props.sideBarOpen ? "" : "d-none"} // Hide with CSS instead
                              >
                                {tab.name}
                              </span>
                            </div>
                            {props.sideBarOpen && tab.count_key && countDetails?.[tab.count_key] !== 0 && (
                              <div>
                                <span className="dashboard_count d-flex justify-content-center">{countDetails?.[tab.count_key]}</span>
                              </div>
                            )}
                          </div>
                        )
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default withRouter(Sidebar);
